import React, { FunctionComponent, useEffect, useState } from "react";
import { trackEvent } from "@leafly-com/web-utils";
import { useRouter } from "next/router";

import ProductLandingPageFAQs from "fixtures/ProductLandingPageFAQs";

import publicConfig from "config/public";
import { ProductMarketplaceCategory } from "custom-types/Product";

import ArticleCard from "components/botanic/ArticleCard";
import Carousel from "components/botanic/Carousel";
import FAQs from "components/FAQ";
import GoogleAd from "components/GoogleAd";
import TrackImpression from "components/TrackImpression";

import getSponsoredArticles, {
  SponsoredArticle,
} from "../../../api/requests/getSponsoredArticles";
import { FeaturedProducts } from "../FeaturedProducts";
import { HeroBanner } from "../HeroBanner";
import { LazyLoadProductsCarouselWithData } from "../LazyLoadProductsCarouselWithData";
import { hasIosParam } from "../lib/paramCheck";
import { ProductCategoryMetaTags } from "../ProductCategoryMetaTags";
import { ProductCategoryNavigation } from "../ProductCategoryNavigation";
import { ProductsCarouselWithData } from "../ProductsCarouselWithData";

export const CAROUSEL_ROUTE_MAP = {
  cartridges: "/products/concentrates/cartridges",
  concentrates: "/products/concentrates",
  edibles: "/products/edibles",
  flowers: "/products/cannabis/flower",
  prerolls: "/products/cannabis/prerolls",
  vapes: "/products/vaping",
} as const;

const {
  imgix: { publicUrl },
} = publicConfig;

const LandingPage: FunctionComponent<{
  categories: ProductMarketplaceCategory[];
  isCanada?: boolean;
}> = ({ categories, isCanada = false }) => {
  const router = useRouter();
  const isIos = hasIosParam(router);
  const [articles, setArticles] = useState<SponsoredArticle[] | null>(null);

  useEffect(() => {
    getSponsoredArticles({ id: 0, sponsorType: "category" }).then(setArticles);
  }, []);

  return (
    <>
      <ProductCategoryMetaTags />
      <ProductCategoryNavigation
        categories={categories}
        isCanada={isCanada}
        isIos={isIos}
      />
      <HeroBanner
        imageSrc={`${publicUrl}/products/category-heroes/product-heroes-v4-1920x500-all-products-1x.jpg`}
      >
        <div className="absolute bottom-0 left-0 text-white">
          <h1 className="heading--xl mb-sm">
            Shop Cannabis Products &amp; Accessories Online
          </h1>
          <p className="landing-hero-paragraph font-bold mb-none text-sm">
            Buy all types of cannabis and CBD products, plus deals available in
            your area.
          </p>
        </div>
      </HeroBanner>
      <div className="container pb-section" data-testid="home-container">
        <GoogleAd
          adPath="/13212770/product/category_hero"
          className="pt-lg"
          sizes={{ desktop: ["fluid"], mobile: ["fluid"] }}
          style={{ minHeight: 188 }}
        />
        <FeaturedProducts className="my-section" />

        <ProductsCarouselWithData
          carouselPath={CAROUSEL_ROUTE_MAP.flowers}
          header="Flower"
          filter={{ subcategory: "flower" }}
        />

        <ProductsCarouselWithData
          carouselPath={CAROUSEL_ROUTE_MAP.edibles}
          header="Edibles"
          filter={{ category: "edibles" }}
        />

        <GoogleAd
          adPath="/13212770/product/product_upper"
          className="mt-section"
          sizes={{ desktop: [728, 90] }}
        />
        <GoogleAd
          adPath="/13212770/product/product_lower"
          className="mt-section"
          sizes={{ mobile: [320, 50] }}
        />

        {!isIos && (
          <LazyLoadProductsCarouselWithData
            height={350}
            offset={100}
            carouselPath={CAROUSEL_ROUTE_MAP.cartridges}
            header="Cartridges"
            filter={{ subcategory: "cartridges" }}
          />
        )}

        <LazyLoadProductsCarouselWithData
          height={350}
          offset={100}
          carouselPath={CAROUSEL_ROUTE_MAP.prerolls}
          header="Pre-rolls"
          filter={{ subcategory: "prerolls" }}
        />

        <LazyLoadProductsCarouselWithData
          height={350}
          offset={100}
          carouselPath={CAROUSEL_ROUTE_MAP.concentrates}
          header="Concentrates"
          filter={{ category: "concentrates" }}
        />

        {!isIos && (
          <LazyLoadProductsCarouselWithData
            height={350}
            offset={100}
            carouselPath={CAROUSEL_ROUTE_MAP.vapes}
            header="Vaping"
            filter={{ category: "vaping" }}
          />
        )}

        <FAQs
          title="Frequently asked questions about buying marijuana products"
          data={ProductLandingPageFAQs}
        />

        <GoogleAd
          adPath="/13212770/product/product_mid"
          className="mt-section"
          sizes={{ mobile: [300, 250] }}
        />

        {!!articles?.length && (
          <div className="mt-section" data-testid="articles">
            {articles.length > 1 ? (
              <Carousel>
                {articles.map((article: SponsoredArticle) => {
                  const {
                    sponsorName,
                    excerpt,
                    date,
                    link,
                    image,
                    title,
                    tags,
                    slug,
                  } = article;
                  return (
                    <TrackImpression
                      category="Product Article"
                      label={slug}
                      key={date}
                    >
                      <ArticleCard
                        key={date}
                        className="mb-md"
                        onClick={() =>
                          trackEvent("Product Article", "Click", slug)
                        }
                        author={`Sponsored by ${sponsorName}`}
                        featured={true}
                        excerpt={excerpt}
                        href={link}
                        tags={tags}
                        title={title}
                        imageUrl={image}
                        sizes={[750]}
                      />
                    </TrackImpression>
                  );
                })}
              </Carousel>
            ) : (
              <TrackImpression
                category="Product Article"
                label={articles[0].slug}
              >
                <ArticleCard
                  key={articles[0].date}
                  className="mb-md w-full elevation-low rounded overflow-hidden block"
                  onClick={() =>
                    trackEvent("Product Article", "Click", articles[0].slug)
                  }
                  author={`Sponsored by ${articles[0].sponsorName}`}
                  featured={true}
                  excerpt={articles[0].excerpt}
                  href={articles[0].link}
                  tags={articles[0].tags}
                  title={articles[0].title}
                  imageUrl={articles[0].image}
                  sizes={[750]}
                />
              </TrackImpression>
            )}
          </div>
        )}
      </div>
      <style jsx>{`
        .landing-hero-location {
          font-family: "GT America Condensed";
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        }

        .landing-hero-paragraph {
          max-width: 420px;
        }
      `}</style>
    </>
  );
};

export default LandingPage;
